import type { AdminQueryGetInput, AdminQueryGetResult } from '@rb/shared/admin-query.types';
import type { GloryShopLogDoc } from '@rb/shared/analytics/glory-log.types';
import { adminQueryGet } from '@/client/admin.client';

export type QueryGetFn = (input: AdminQueryGetInput) => Promise<AdminQueryGetResult>;

/** admin.queryGet 의 서버측 클램프 상한. */
const PAGE_SIZE = 100;
/** 페이징 폭주 방어. 100 × 200 = 2만 행이면 현재 유입량 기준 수년치다. */
const DEFAULT_MAX_PAGES = 200;

export interface FetchGloryLogsResult {
  logs: GloryShopLogDoc[];
  /** true 면 fromMs 경계나 hasMore=false 에 닿기 전에 maxPages 상한으로 조회가 끊긴 것 —
   * 남은 구간이 집계에서 빠져 과소집계될 수 있으므로 호출부가 반드시 사용자에게 알려야 한다. */
  truncated: boolean;
}

/**
 * `gloryShopLog` 를 timestamp 내림차순으로 페이징해 읽는다.
 *
 * 필터를 걸지 않는 이유: 이 컬렉션엔 글로리 행만 있어서 필터가 불필요하고, 필터+정렬
 * 조합은 플랫폼 Firestore 복합 인덱스를 요구해 실패한다(실측). 기간 제한은 내림차순
 * 특성을 이용해 `fromMs` 이전 행을 만나는 순간 중단하는 방식으로 건다.
 */
export async function fetchGloryLogs(opts: {
  fromMs: number | null;
  maxPages?: number;
  onProgress?: (loaded: number) => void;
  queryGet?: QueryGetFn;
}): Promise<FetchGloryLogsResult> {
  const query = opts.queryGet ?? adminQueryGet;
  const maxPages = opts.maxPages ?? DEFAULT_MAX_PAGES;
  const seen = new Set<string>();
  const out: GloryShopLogDoc[] = [];
  let cursor: string | number | null = null;
  let truncated = true;

  for (let page = 0; page < maxPages; page += 1) {
    const res = await query({
      collection: 'gloryShopLog',
      orderBy: { field: 'timestamp', direction: 'desc' },
      limit: PAGE_SIZE,
      cursor,
    });

    let reachedEdge = false;
    for (const record of res.records ?? []) {
      const doc = record as unknown as GloryShopLogDoc & { __id: string };
      if (opts.fromMs !== null && doc.timestamp < opts.fromMs) {
        reachedEdge = true;
        break;
      }
      if (seen.has(doc.__id)) continue;
      seen.add(doc.__id);
      out.push(doc);
    }

    opts.onProgress?.(out.length);
    if (reachedEdge || !res.hasMore) {
      truncated = false;
      break;
    }
    cursor = res.nextCursor ?? null;
  }

  return { logs: out, truncated };
}
